import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import type { RealtimeConnection, RealtimeDataSource, RefreshReason } from '../api/data-source'
import { isAbortError } from '../api/shared-resource'
import { HIGHLIGHT_MS, LOCATION_THROTTLE_MS, OFFLINE_AFTER_FAILED_POLLS, realtimeConfig } from '../config'
import { useRealtimeStore } from '../store/realtime-store'
import type { ConnectionStatus, Job, RealtimeEvent, RealtimeSnapshot } from '../types'
import { createKeyedThrottle } from '../utils/throttle'
import { useDocumentHidden } from './use-environment'
import { schedulingRules, SHOW_WORKER_RESPONSE_STATUS } from '../schedulingRules'
import { consumeLocalMutation } from './local-mutations'
import { clearStaffingMutations, protectOptimisticStaffing } from './staffing-guard'

const JOB_STATUS_TEXT: Record<Job['status'], string> = {
  unassigned: 'unassigned',
  scheduled: 'scheduled',
  en_route: 'en route',
  in_progress: 'in progress',
  completed: 'completed',
  late: 'running late',
}

type LoadMode = RefreshReason

/** Refreshes that are skipped (not queued) while another refresh is still running. */
const SKIP_WHILE_BUSY: LoadMode[] = ['poll', 'resync']
/** Refreshes whose result is applied without highlighting / "new job" toasts. */
const QUIET_MODES: LoadMode[] = ['initial', 'mutation']

export interface RealtimeSyncState {
  connection: ConnectionStatus
  /** True while the browser tab is hidden (updates are paused). */
  isPaused: boolean
  /** Epoch ms of the last applied event or successful fetch. */
  lastUpdatedAt: number | null
  isInitialLoading: boolean
  isRefreshing: boolean
  error: Error | null
  /** Whether a push channel exists (false = polling only). */
  hasPushChannel: boolean
  /** Retry button: bypasses every cache. */
  retry: () => void
  /** Refresh button: bypasses every cache. */
  refresh: () => void
  /** Re-reads assignments right away (after the admin's own Assign / Remove). */
  refreshStaffing: () => void
}

interface EventEffect {
  jobId: string
  toast?: { message: string; tone: 'info' | 'warning' }
  /** Flash the whole job card. */
  highlightCard?: boolean
  /** Flash one worker row (1.5s) inside the card. */
  flashWorkerId?: string
  /** Text for the aria-live region. */
  announce?: string
  /** The expanded job was completed by someone else. */
  notice?: 'completed'
}

const RESPONSE_TEXT = { accepted: 'accepted', declined: 'declined', expired: 'did not respond to', pending: 'has a pending offer for' } as const
const WORKER_STATUS_TEXT = { assigned: 'is assigned to', checked_in: 'checked in at', on_the_way: 'is on the way to', started: 'started', completed: 'completed' } as const

/**
 * Side effects (toast / flash / announcement) for job events, computed against the pre-event
 * state. Echoes of changes made from this tab are skipped — the action already reported them.
 */
function describeJobEvent(event: RealtimeEvent): EventEffect | null {
  const { jobsById, workersById, selectedJobId } = useRealtimeStore.getState()
  const nameOf = (workerId: string) => workersById[workerId]?.name ?? 'A worker'

  switch (event.type) {
    case 'job.created':
      return { jobId: event.job.id, highlightCard: true, toast: { message: `New job ${event.job.bookingNo} · ${event.job.service}`, tone: 'info' } }

    case 'job.updated': {
      const previous = jobsById[event.job.id]
      if (!previous || !event.job.status || event.job.status === previous.status) return null
      const message = `${previous.bookingNo} is now ${JOB_STATUS_TEXT[event.job.status]}`
      return {
        jobId: previous.id,
        highlightCard: true,
        toast: { message, tone: 'info' },
        announce: message,
        notice: event.job.status === 'completed' && selectedJobId === previous.id ? 'completed' : undefined,
      }
    }

    case 'job.deleted': {
      const previous = jobsById[event.jobId]
      return previous ? { jobId: previous.id, toast: { message: `${previous.bookingNo} was removed`, tone: 'info' }, announce: `${previous.bookingNo} was removed` } : null
    }

    case 'job.assigned':
    case 'job.worker.added': {
      const workerId = event.type === 'job.assigned' ? event.workerId : event.worker.workerId
      if (consumeLocalMutation('add', event.jobId, workerId)) return null
      const job = jobsById[event.jobId]
      if (!job) return null
      const existing = job.workers.find((worker) => worker.workerId === workerId)
      if (existing) {
        const opened = SHOW_WORKER_RESPONSE_STATUS && event.type === 'job.worker.added' && event.worker.openedAt && !existing.openedAt
        return opened ? { jobId: job.id, flashWorkerId: workerId, announce: `${nameOf(workerId)} opened the offer for ${job.bookingNo}` } : null
      }
      const message = `${nameOf(workerId)} added to ${job.bookingNo}`
      return { jobId: job.id, highlightCard: true, flashWorkerId: workerId, toast: { message, tone: 'info' }, announce: message }
    }

    case 'job.worker.removed': {
      if (consumeLocalMutation('remove', event.jobId, event.workerId)) return null
      const job = jobsById[event.jobId]
      if (!job) return null
      const message = `${nameOf(event.workerId)} removed from ${job.bookingNo}`
      return { jobId: job.id, highlightCard: true, toast: { message, tone: 'info' }, announce: message }
    }

    case 'job.worker.response': {
      const job = jobsById[event.jobId]
      if (!job) return null
      if (!SHOW_WORKER_RESPONSE_STATUS) {
        // Response UI is off: only say when a worker drops off the job (declined / expired).
        if (event.response !== 'declined' && event.response !== 'expired') return null
        const message = `${nameOf(event.workerId)} is no longer on ${job.bookingNo}`
        return { jobId: job.id, highlightCard: true, toast: { message, tone: 'warning' }, announce: message }
      }
      const reason = event.declineReason ? ` (${event.declineReason})` : ''
      const message = `${nameOf(event.workerId)} ${RESPONSE_TEXT[event.response]} ${job.bookingNo}${event.response === 'declined' ? reason : ''}`
      return {
        jobId: job.id,
        flashWorkerId: event.workerId,
        announce: message,
        toast: event.response === 'declined'
          ? { message, tone: 'warning' }
          : event.response === 'accepted' ? { message, tone: 'info' } : undefined,
      }
    }

    case 'job.worker.status': {
      const job = jobsById[event.jobId]
      if (!job) return null
      return { jobId: job.id, flashWorkerId: event.workerId, announce: `${nameOf(event.workerId)} ${WORKER_STATUS_TEXT[event.status]} ${job.bookingNo}` }
    }

    case 'job.worker.reminded':
      return consumeLocalMutation('remind', event.jobId, event.workerId) ? null : { jobId: event.jobId, flashWorkerId: event.workerId }

    case 'job.required.changed': {
      if (consumeLocalMutation('required', event.jobId)) return null
      const job = jobsById[event.jobId]
      return job ? { jobId: job.id, highlightCard: true, announce: `${job.bookingNo} now needs ${event.requiredWorkers} workers` } : null
    }

    default:
      return null
  }
}

/**
 * Keeps the realtime store in sync: initial snapshot, push events (WebSocket) with automatic
 * reconnect, polling fallback, pause while the tab is hidden and resync when it returns.
 */
export function useRealtimeSocket({
  source,
  dateKey,
  timeZone,
}: {
  source: RealtimeDataSource
  dateKey: string
  timeZone: string
}): RealtimeSyncState {
  const isHidden = useDocumentHidden()
  const [connection, setConnection] = useState<ConnectionStatus>('connecting')
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null)
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [socketOpen, setSocketOpen] = useState(false)
  const [hasPushChannel, setHasPushChannel] = useState(false)

  const requestSeqRef = useRef(0)
  const failedPollsRef = useRef(0)
  const hydratedRef = useRef(false)
  const hasPushRef = useRef(false)
  const socketOpenRef = useRef(false)
  const socketFailedRef = useRef(false)
  const hiddenRef = useRef(isHidden)
  const connectionRef = useRef<RealtimeConnection | null>(null)
  const queueRef = useRef<RealtimeEvent[]>([])
  const frameRef = useRef<number | null>(null)
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  hiddenRef.current = isHidden

  const scheduleHighlightPrune = useCallback(() => {
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current)
    highlightTimerRef.current = setTimeout(() => {
      useRealtimeStore.getState().pruneHighlights(Date.now())
    }, Math.max(HIGHLIGHT_MS, schedulingRules.rowFlashMs) + 50)
  }, [])

  const flushQueue = useCallback(() => {
    frameRef.current = null
    const batch = queueRef.current
    if (batch.length === 0) return
    queueRef.current = []
    useRealtimeStore.getState().applyEvents(batch)
    setLastUpdatedAt(Date.now())
  }, [])

  const enqueue = useCallback((event: RealtimeEvent) => {
    queueRef.current.push(event)
    if (frameRef.current == null) {
      frameRef.current = typeof requestAnimationFrame === 'function'
        ? requestAnimationFrame(flushQueue)
        : (setTimeout(flushQueue, 16) as unknown as number)
    }
  }, [flushQueue])

  const throttleRef = useRef<ReturnType<typeof createKeyedThrottle<RealtimeEvent>> | null>(null)
  if (!throttleRef.current) {
    throttleRef.current = createKeyedThrottle<RealtimeEvent>(LOCATION_THROTTLE_MS, (event) => enqueue(event))
  }

  const handleEvent = useCallback((event: RealtimeEvent) => {
    // Paused: drop events; a full resync runs when the tab becomes visible again.
    if (hiddenRef.current || !hydratedRef.current) return

    if (event.type === 'worker.location.updated' || event.type === 'worker.location') {
      throttleRef.current?.push(event.workerId, event)
      return
    }

    const effect = describeJobEvent(event)
    enqueue(event)
    if (!effect) return

    const store = useRealtimeStore.getState()
    if (effect.highlightCard) store.highlightJobs([effect.jobId], HIGHLIGHT_MS)
    if (effect.flashWorkerId) store.flashRows([`${effect.jobId}|${effect.flashWorkerId}`], schedulingRules.rowFlashMs)
    if (effect.highlightCard || effect.flashWorkerId) scheduleHighlightPrune()
    if (effect.announce) store.announce(effect.announce)
    if (effect.notice) store.setSelectionNotice({ jobId: effect.jobId, kind: effect.notice })
    if (effect.toast) {
      const show = effect.toast.tone === 'warning' ? toast.warning : toast
      show(effect.toast.message, { id: `rt-${effect.jobId}-${effect.flashWorkerId ?? 'job'}`, duration: effect.toast.tone === 'warning' ? 6_000 : 3_000 })
    }
  }, [enqueue, scheduleHighlightPrune])

  /** Aborted on unmount and when the branch / day changes. */
  const lifecycleRef = useRef<AbortController | null>(null)
  /** The refresh currently running (one at a time for polls / tab return). */
  const inflightRef = useRef<Promise<void> | null>(null)

  /** Applies a snapshot, keeping what is on screen stable (patch merge, own changes protected). */
  const applySnapshot = useCallback((snapshot: RealtimeSnapshot, quiet: boolean) => {
    const store = useRealtimeStore.getState()
    const guarded = protectOptimisticStaffing(snapshot, store.jobsById)
    if (!hydratedRef.current) {
      store.hydrate(guarded)
      hydratedRef.current = true
      setIsInitialLoading(false)
    } else {
      const { newJobIds, changedJobIds } = store.merge(guarded)
      const touched = [...newJobIds, ...changedJobIds]
      if (!quiet && touched.length > 0) {
        store.highlightJobs(touched, HIGHLIGHT_MS)
        scheduleHighlightPrune()
      }
      if (!quiet && newJobIds.length > 0) {
        toast(newJobIds.length === 1 ? 'New job added' : `${newJobIds.length} new jobs added`, { id: 'rt-new-jobs', duration: 3_000 })
      }
    }
    store.setStaffingLoaded(guarded.staffingLoaded !== false)
    setLastUpdatedAt(Date.now())
  }, [scheduleHighlightPrune])

  const load = useCallback((mode: LoadMode): Promise<void> => {
    const lifecycle = lifecycleRef.current
    if (!lifecycle || lifecycle.signal.aborted) return Promise.resolve()
    // One refresh at a time: a poll / tab return while one is running is simply skipped.
    if (inflightRef.current && SKIP_WHILE_BUSY.includes(mode)) return inflightRef.current
    const { signal } = lifecycle
    const seq = ++requestSeqRef.current
    const isCurrent = () => seq === requestSeqRef.current && !signal.aborted
    if (mode === 'resync' || mode === 'manual') setIsRefreshing(true)

    const run = (async () => {
      try {
        const snapshot = await source.fetchSnapshot({
          dateKey,
          timeZone,
          reason: mode,
          signal,
          // Fast first snapshot (and background details): applied quietly, no flicker.
          onProgress: (partial) => {
            if (isCurrent()) applySnapshot(partial, true)
          },
        })
        if (!isCurrent()) return
        applySnapshot(snapshot, QUIET_MODES.includes(mode))
        failedPollsRef.current = 0
        setError(null)
        setConnection(
          !hasPushRef.current || socketOpenRef.current
            ? 'live'
            : socketFailedRef.current ? 'reconnecting' : 'connecting',
        )
      } catch (loadError) {
        // Cancelled (unmount / day change / superseded): nothing to report.
        if (!isCurrent() || isAbortError(loadError)) return
        failedPollsRef.current += 1
        // Previously loaded data stays on screen; only the pill changes.
        if (!hydratedRef.current) {
          setError(loadError instanceof Error ? loadError : new Error('Unable to load realtime data.'))
          setConnection('offline')
        } else {
          setConnection(failedPollsRef.current >= OFFLINE_AFTER_FAILED_POLLS ? 'offline' : 'reconnecting')
        }
      } finally {
        if (seq === requestSeqRef.current) {
          setIsInitialLoading(false)
          setIsRefreshing(false)
        }
      }
    })()
    const tracked = run.finally(() => {
      if (inflightRef.current === tracked) inflightRef.current = null
    })
    inflightRef.current = tracked
    return tracked
  }, [applySnapshot, dateKey, source, timeZone])

  // Initial load, and a fresh load whenever the source/day/timezone changes. The cleanup aborts
  // this generation's requests; under StrictMode the immediate remount re-joins them instead.
  useEffect(() => {
    const lifecycle = new AbortController()
    lifecycleRef.current = lifecycle
    inflightRef.current = null
    hydratedRef.current = false
    failedPollsRef.current = 0
    setIsInitialLoading(true)
    setError(null)
    void load('initial')
    return () => {
      lifecycle.abort()
      if (lifecycleRef.current === lifecycle) lifecycleRef.current = null
      inflightRef.current = null
      requestSeqRef.current += 1
    }
  }, [load])

  // Push channel. Closed while the tab is hidden, reopened when it is visible again.
  useEffect(() => {
    if (isHidden) return undefined

    const conn = source.connect({
      onOpen: () => {
        socketOpenRef.current = true
        socketFailedRef.current = false
        setSocketOpen(true)
        if (hydratedRef.current) setConnection('live')
      },
      onClose: ({ willRetry }) => {
        socketOpenRef.current = false
        socketFailedRef.current = true
        setSocketOpen(false)
        setConnection(willRetry ? 'reconnecting' : 'offline')
      },
      onEvent: handleEvent,
    })

    connectionRef.current = conn
    hasPushRef.current = conn != null
    setHasPushChannel(conn != null)

    return () => {
      conn?.close()
      connectionRef.current = null
      socketOpenRef.current = false
      setSocketOpen(false)
    }
  }, [handleEvent, isHidden, source])

  // Resync once when the tab becomes visible again after being hidden.
  const wasHiddenRef = useRef(isHidden)
  useEffect(() => {
    if (wasHiddenRef.current && !isHidden && hydratedRef.current) {
      void load('resync')
    }
    wasHiddenRef.current = isHidden
  }, [isHidden, load])

  // Polling fallback: only while visible and without an open push channel.
  const pollingActive = !isHidden && !socketOpen && !isInitialLoading
  useEffect(() => {
    if (!pollingActive) return undefined
    const id = setInterval(() => void load('poll'), realtimeConfig.pollIntervalMs)
    return () => clearInterval(id)
  }, [load, pollingActive])

  // Browser connectivity.
  useEffect(() => {
    const handleOffline = () => setConnection('offline')
    const handleOnline = () => {
      connectionRef.current?.reconnectNow()
      void load('resync')
    }
    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)
    return () => {
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('online', handleOnline)
    }
  }, [load])

  // Cleanup timers and pending frames on unmount.
  useEffect(() => () => {
    clearStaffingMutations()
    throttleRef.current?.cancel()
    if (frameRef.current != null && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frameRef.current)
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current)
    queueRef.current = []
  }, [])

  const retry = useCallback(() => {
    failedPollsRef.current = 0
    setConnection('connecting')
    connectionRef.current?.reconnectNow()
    void load('manual')
  }, [load])

  const refresh = useCallback(() => {
    void load('manual')
  }, [load])

  const refreshStaffing = useCallback(() => {
    void load('mutation')
  }, [load])

  return {
    connection,
    isPaused: isHidden,
    lastUpdatedAt,
    isInitialLoading,
    isRefreshing,
    error,
    hasPushChannel,
    retry,
    refresh,
    refreshStaffing,
  }
}
